import { useState } from "react";
import { Loader2, CheckCircle2 } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { track } from "@/lib/analytics";

const ROLES = ["Board member", "Property manager", "Resident"];
const EMPTY = { building_name: "", contact_name: "", role: "", phone: "", email: "", address: "", num_units: "", message: "" };

export const AssociationForm = () => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      await api.post("/association-leads", form);
      track("association_lead_submit", { role: form.role });
      setDone(true);
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <div data-testid="association-form-success" className="bg-white border border-zinc-200 p-8 text-center rounded-sm">
        <CheckCircle2 className="w-12 h-12 text-brand mx-auto mb-4" />
        <h3 className="font-heading font-bold text-2xl text-brand-ink mb-2">Request received</h3>
        <p className="text-zinc-600">Thanks! We'll contact you to set up your building's partner program. Questions? Call <a href="tel:7865121929" className="text-brand font-medium">786-512-1929</a>.</p>
      </div>
    );
  }

  return (
    <form data-testid="association-form" onSubmit={submit} className="bg-white border-2 border-brand p-6 sm:p-8 rounded-sm grid grid-cols-1 sm:grid-cols-2 gap-4">
      <Input data-testid="af-building-name" required placeholder="Association or building name" value={form.building_name} onChange={set("building_name")} className="rounded-sm sm:col-span-2" />
      <Input data-testid="af-contact-name" required placeholder="Contact name" value={form.contact_name} onChange={set("contact_name")} className="rounded-sm" />
      <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} required>
        <SelectTrigger data-testid="af-role" className="rounded-sm"><SelectValue placeholder="Role" /></SelectTrigger>
        <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}</SelectContent>
      </Select>
      <Input data-testid="af-phone" required type="tel" placeholder="Phone" value={form.phone} onChange={set("phone")} className="rounded-sm" />
      <Input data-testid="af-email" required type="email" placeholder="Email" value={form.email} onChange={set("email")} className="rounded-sm" />
      <Input data-testid="af-address" required placeholder="Building address" value={form.address} onChange={set("address")} className="rounded-sm" />
      <Input data-testid="af-num-units" required type="number" min="1" placeholder="Number of units" value={form.num_units} onChange={set("num_units")} className="rounded-sm" />
      <Textarea data-testid="af-message" placeholder="Message (optional)" value={form.message} onChange={set("message")} className="sm:col-span-2 rounded-sm" rows={3} />
      {error && <p data-testid="af-error" className="sm:col-span-2 text-sm text-brand-red">{error}</p>}
      <button data-testid="af-submit-btn" type="submit" disabled={loading}
        className="sm:col-span-2 bg-brand hover:bg-brand-dark disabled:opacity-60 text-white font-bold py-4 rounded-sm transition-colors duration-200 flex items-center justify-center gap-2">
        {loading && <Loader2 className="w-4 h-4 animate-spin" />} Join the Program
      </button>
    </form>
  );
};
